import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import SidebarToggle from '@/components/SidebarToggle'
import { BookIcon, ChevronLeftIcon, ChevronRightIcon } from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'
import ProjectContextTab from './components/ProjectContextTab'
import ProjectMeetingsTab from './components/ProjectMeetingsTab'
import ProjectMembersTab from './components/ProjectMembersTab'
import ProjectOverviewTab from './components/ProjectOverviewTab'
import ProjectWeeklyReportTab from './components/ProjectWeeklyReportTab'
import { useProjectDetail } from './useProjectDetail'

type DetailTab = 'overview' | 'members' | 'meetings' | 'context' | 'weekly'

function tabClass(active: boolean): string {
  return [
    '-mb-px flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors',
    active
      ? 'border-[var(--accent)] text-[var(--accent)]'
      : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
  ].join(' ')
}

/** The count disc beside a tab label; absent rather than `0` while unknown. */
function TabBadge({ count }: { count: number | null }) {
  if (count === null) return null
  return (
    <span className="rounded-full bg-[var(--bg-tertiary)] px-2 py-0.5 text-xs font-semibold tabular-nums text-[var(--text-secondary)]">
      {count}
    </span>
  )
}

/**
 * One project's screen: its own facts, who is on it, the meetings recorded
 * against it, and (mock) project context.
 *
 * Replaces the card's old jump straight to Backlog. The meetings are this
 * product's own and were not visible there at all, which is the whole reason
 * for the screen.
 */
export default function ProjectDetailPage() {
  const { t } = useUiStrings()
  const navigate = useNavigate()
  const { projectKey = '' } = useParams<{ projectKey: string }>()
  const detail = useProjectDetail(projectKey)
  const [tab, setTab] = useState<DetailTab>('overview')

  const { project, state } = detail
  const title = project?.name ?? projectKey
  const needsKey = state === 'connect' || state === 'reauth'

  return (
    <>
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 sm:px-6">
        <SidebarToggle />
        <nav aria-label={t.projectManagement} className="flex min-w-0 items-center gap-2">
          <Link
            to="/projects"
            className="shrink-0 text-sm text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
          >
            {t.projectManagement}
          </Link>
          <ChevronRightIcon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" />
          <span className="min-w-0 truncate text-sm font-bold text-[var(--text-primary)]">
            {title}
          </span>
        </nav>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
        <div className="flex min-w-0 items-start gap-3">
          <button
            type="button"
            aria-label={t.projectDetailBack}
            onClick={() => navigate('/projects')}
            className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-secondary)] transition-colors hover:border-[var(--border-hover)] hover:text-[var(--text-primary)] sm:h-10 sm:w-10 sm:rounded-xl"
          >
            <ChevronLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="truncate text-lg font-bold tracking-tight text-[var(--text-primary)]">
                {title}
              </h1>
              {state === 'ready' && (
                <span className="rounded-full border border-indigo-100 bg-[var(--accent-light)] px-2.5 py-0.5 text-xs text-[var(--accent)] dark:border-indigo-900">
                  {t.projectDetailStatusRunning}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-[var(--text-secondary)] tabular-nums">{projectKey}</p>
          </div>
        </div>

        {state === 'loading' && (
          <p className="mt-6 text-sm text-[var(--text-secondary)]">{t.projectLoading}</p>
        )}

        {state === 'missing' && (
          <p className="mt-6 text-sm text-[var(--text-secondary)]">{t.projectDetailNotFound}</p>
        )}

        {state === 'error' && (
          <p
            role="alert"
            className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs font-medium text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300"
          >
            {detail.errorMessage ?? t.failedLoadProjects}
          </p>
        )}

        {needsKey && (
          <p className="mt-6 text-sm text-[var(--text-secondary)]">
            {state === 'connect' ? t.projectConnectRequired : t.projectReauthRequired}.{' '}
            <Link
              to="/settings"
              className="font-semibold text-[var(--accent)] underline underline-offset-2"
            >
              {t.projectOpenSettings}
            </Link>
          </p>
        )}

        {state === 'ready' && project && (
          <>
            <div
              role="tablist"
              aria-label={t.projectManagement}
              className="mt-5 flex gap-1 overflow-x-auto border-b border-[var(--border-color)]"
            >
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'overview'}
                onClick={() => setTab('overview')}
                className={tabClass(tab === 'overview')}
              >
                {t.projectDetailOverview}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'members'}
                onClick={() => setTab('members')}
                className={tabClass(tab === 'members')}
              >
                {t.projectDetailMembers}
                <TabBadge count={detail.membersLoaded ? detail.members.length : null} />
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'meetings'}
                onClick={() => setTab('meetings')}
                className={tabClass(tab === 'meetings')}
              >
                {t.projectDetailMeetings}
                <TabBadge count={project.meetingCount} />
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'context'}
                onClick={() => setTab('context')}
                className={tabClass(tab === 'context')}
              >
                <BookIcon className="h-4 w-4 shrink-0" />
                {t.projectDetailContextTab}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'weekly'}
                onClick={() => setTab('weekly')}
                className={tabClass(tab === 'weekly')}
              >
                {t.weeklyReportTab}
              </button>
            </div>

            <div className="mt-5" role="tabpanel">
              {tab === 'overview' && <ProjectOverviewTab project={project} />}
              {tab === 'weekly' && <ProjectWeeklyReportTab projectKey={project.projectKey} />}
              {tab === 'members' && (
                <ProjectMembersTab
                  members={detail.members}
                  loaded={detail.membersLoaded}
                  failed={detail.membersFailed}
                />
              )}
              {/* Mounted only once its tab is open, which is what keeps the
                  meeting list from being fetched for a reader who never asks
                  for it. The other two tabs are already in hand. */}
              {tab === 'meetings' && (
                <ProjectMeetingsTab
                  projectKey={project.projectKey}
                  onMeetingsChanged={detail.reload}
                />
              )}
              {tab === 'context' && <ProjectContextTab projectKey={project.projectKey} />}
            </div>
          </>
        )}
      </div>
    </>
  )
}
